import React, { useEffect, useMemo, useState } from "react";
import { PinkWrapper, FormFooter, FormInputDiv, FormsFlex, FormStyle, PageTitle, SecondSectionTitle, Subliner, Wrapper } from "./style";
import { ReactComponent as InfoIcon } from "../../assets/icons/info2.svg";
import { ReactComponent as KlashaLogo } from "../../assets/icons/logo.svg";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { submitPinkForm } from "./pinkAPI";
import { AxiosError } from "axios";
import { formatDate } from "../../utils/dateUtils";
import NetworkErrorAlert from "../../components/common/alerts/NetworkErrorAlert";
import SuccessAlert from "../../components/common/alerts/SuccessAlert";
import Loader from "../../components/common/Loader";
import { Input } from "../../components/common/Input";
import { InputPhone } from "../../components/common/InputPhone";
import CustomDatePicker from "../../components/common/CustomDateRangePicker/CustomDatePicker";
import { Tooltip } from "antd";
import { Button } from "../../components/common/Button";
import { getAccountName, getBankList } from "../Settings/settingsAPI";
import CustomSelect from "../../components/common/CustomSelect";

type Inputs = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    dob: string;
    virtualAccountBalance: string;
    bankName: string;
    accountNumber: string;
    accountName: string;
};

const PinkEndOfLife: React.FC = () => {

    const {
        handleSubmit,
        control,
        formState: { errors, isValid },
        reset,
        watch,
        setValue
    } = useForm<Inputs>({
        mode: "onChange",
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
            dob: "",
            virtualAccountBalance: "",
            bankName: undefined,
            accountNumber: "",
            accountName: ""
        }
    });

    const mailformat = /^\w+([._+-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/;

    const tooltipText = 'Bank details must match your registered information on our platform such as account name and account number.'

    const accountNumber = watch("accountNumber");
    const bankName = watch("bankName");
    const [ formState, setFormState ] = useState({
        accountResolveError: "",
        showAccountName: false,
    })

    const { data: listOfBanks } = useQuery(['bankList', 'NG'], () => getBankList('NG'))

    const {bankLookupMap, mappedBankList} = useMemo(() => {
        const bankMap = {} as Record<string, string>
        const bankArray = listOfBanks?.map((bank) => {
            bankMap[bank.name] = bank.code
            return {label: bank.name, value: bank.name}
        }) || []
        return {bankLookupMap: bankMap, mappedBankList: bankArray}
    }, [listOfBanks])

    const selectedBankCode = bankName ? bankLookupMap[bankName] : ""

    const { isLoading } = useQuery(['userAccountName', accountNumber, selectedBankCode],
        () => getAccountName(accountNumber, selectedBankCode),
        {
            enabled: !!(accountNumber?.length === 10 && selectedBankCode),
            onSuccess: (response) => {
                if (response?.status === "error") {
                    setFormState((prev) => ({
                        ...prev,
                        accountResolveError: `${response.message}, check details again`,
                        showAccountName: false
                    }))
                    return
                } 
                setFormState((prev) => ({
                    ...prev,
                    accountResolveError: '',
                    showAccountName: true
                }))
                setValue('accountName', response?.data?.account_name, { shouldValidate: true });
            }
        }
    );

    const mutation = useMutation({
        mutationFn: (data: Inputs) => {
            return submitPinkForm({
                ...data,
                dob: formatDate(data.dob),
            });
        },
        onSuccess: () => {
            reset();
            setTimeout(() => {
                window.location.replace('https://www.klasha.com/');
            }, 5000)
        }
    });

    useEffect(() => {
        if (mutation.isError || mutation.isSuccess || formState.accountResolveError) {
            const timer = setTimeout(() => {
                setFormState((prev) => ({
                    ...prev,
                    accountResolveError: null
                }))
                mutation.reset()
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [mutation.isError, mutation.isSuccess, formState.accountResolveError]);

    const errorMessage = useMemo(() => {
        if (mutation.isError) {
            const error = mutation.error as AxiosError;
            const errorObj = error?.response?.data as any;
            return errorObj?.message || "An error occurred";
        }
        return "";
    }, [mutation.isError, mutation.error]);

    const successMessage = "Your pink card has been successfully defunded and closed";

    return (
        <PinkWrapper>
            <Loader isLoading={isLoading}/>
            <KlashaLogo />
            <Wrapper>
                {(mutation.isError || formState.accountResolveError) && (
                    <NetworkErrorAlert message={errorMessage || formState.accountResolveError} />
                )}
                {mutation.isSuccess && (
                    <SuccessAlert message={successMessage} />
                )}
                <Loader isLoading={mutation.isLoading} />
                <PageTitle>Klasha APP Pink Card Withdrawal Request</PageTitle>
                <Subliner>Exclusive to Pink Card Users</Subliner>

                <FormStyle onSubmit={handleSubmit((data) => mutation.mutate(data))}>
                    <FormsFlex>
                        <FormInputDiv>
                            <Controller
                                name="firstName"
                                control={control}
                                rules={{
                                    required: "First name is required",
                                }}
                                render={({ field }) => (
                                    <Input
                                        label="First name"
                                        onChange={field.onChange}
                                        hasError={!!errors.firstName}
                                        value={field.value}
                                    />
                                )}
                            />
                            {errors.firstName && <span>{errors.firstName.message}</span>}
                        </FormInputDiv>

                        <FormInputDiv>
                            <Controller
                                name="lastName"
                                control={control}
                                rules={{
                                    required: "Last name is required",
                                }}
                                render={({ field }) => (
                                    <Input
                                        label="Last name"
                                        onChange={field.onChange}
                                        value={field.value}
                                        hasError={!!errors.lastName}
                                    />
                                )}
                            />
                            {errors.lastName && <span>{errors.lastName.message}</span>}
                        </FormInputDiv>
                    </FormsFlex>

                    <FormInputDiv
                        $hasError={["required"].indexOf(errors.phoneNumber?.type) > -1}
                    >
                        <Controller
                            name="phoneNumber"
                            control={control}
                            rules={{
                                required: "Phone Number is required",
                            }}
                            render={({ field }) => (
                                <InputPhone
                                    height="46px"
                                    label="Phone number"
                                    placeholder="Phone number"
                                    field={field}
                                    hasError={!!errors.phoneNumber}
                                />
                            )}
                        />
                        {errors.phoneNumber && <span>{errors.phoneNumber.message}</span>}
                    </FormInputDiv>

                    <FormInputDiv>
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                required: "Email is required",
                                pattern: {
                                    value: mailformat,
                                    message: "Please enter a valid email address",
                                },
                            }}
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    hasError={!!errors.email}
                                    value={field.value}
                                    onChange={field.onChange}
                                    id="email"
                                    label="Email"
                                    tooltipText="Email used on your Klasha mobile app account."
                                />
                            )}
                        />
                        {errors.email && <span>{errors.email.message}</span>}
                    </FormInputDiv>

                    <FormInputDiv>
                        <Controller
                            name="dob"
                            control={control}
                            rules={{ required: "Date of birth is required" }}
                            render={({ field }) => (
                                <CustomDatePicker
                                    value={field.value}
                                    onChange={(date) => field.onChange(date)}
                                    placeholder=""
                                    animatedLabel="Date of birth"
                                    lightBorder={true}
                                />
                            )}
                        />
                        {errors.dob && <span>{errors.dob.message}</span>}
                    </FormInputDiv>

                    <FormInputDiv>
                        <Controller
                            name="virtualAccountBalance"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="number"
                                    value={field.value}
                                    onChange={field.onChange}
                                    id="pinkVirtualCardBalance"
                                    label="Pink virtual card balance (optional)"
                                    tooltipText="Enter the remaining balance on your Pink virtual card"
                                />
                            )}
                        />
                    </FormInputDiv>

                    <PageTitle>
                        <SecondSectionTitle>Bank information</SecondSectionTitle>
                        <Tooltip
                            overlayClassName="input-tooltip"
                            placement="right"
                            title={tooltipText}
                            trigger={["hover", "click"]}
                        >
                            <InfoIcon width={16} height={16} />
                        </Tooltip>
                    </PageTitle>

                    <FormInputDiv>
                        <Controller
                            name="bankName"
                            control={control}
                            rules={{
                                required: "Bank name is required",
                            }}
                            render={({ field }) => (
                                <CustomSelect
                                    placeholder="Bank name"
                                    $width="100%"
                                    field={field}
                                    disableLabel
                                    showSearch
                                    filterOption={(inputValue, option) =>
                                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                                    }
                                    lightBorder={true}
                                    options={mappedBankList}
                                />
                            )}
                        />
                        {errors.bankName && <span>{errors.bankName.message}</span>}
                    </FormInputDiv>

                    <FormInputDiv>
                        <Controller
                            name="accountNumber"
                            control={control}
                            rules={{
                                required: "Account number is required",
                                validate: {
                                    validLength: value => value.length === 10 || "Account number must be 10 digits"
                                }
                            }}
                            render={({ field }) => (
                                <Input
                                    label="Account number"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        field.onChange(e)
                                    }
                                    }
                                    value={field.value}
                                    hasError={!!errors.accountNumber}
                                    type="number"
                                />
                            )}
                        />
                        {errors.accountNumber && <span>{errors.accountNumber.message}</span>}
                    </FormInputDiv>

                    {formState.showAccountName && <FormInputDiv>
                        <Controller
                            name="accountName"
                            control={control}
                            rules={{
                                required: "Account name is required"
                            }}
                            render={({ field }) => (
                                <Input
                                    disabled
                                    label="Account name"
                                    onChange={field.onChange}
                                    value={field.value}
                                    hasError={!!errors.accountName}
                                />
                            )}
                        />
                        {errors.accountName && <span>{errors.accountName.message}</span>}
                    </FormInputDiv>}

                    <FormFooter>
                        <Button
                            label="Submit"
                            type="submit"
                            width="100%"
                            height="40px"
                            fontSize="12px"
                            disabled={!isValid || isLoading}
                        />

                    </FormFooter>
                </FormStyle>
            </Wrapper>
        </PinkWrapper>
    )
}

export default PinkEndOfLife